import { DOWNLOAD_INVITE_SPREADSHEET } from 'constants/endpoints';
import { useFormBehaviors } from 'hooks';
import {
  UseFormBehaviors,
  UseFormBehaviorsEventType,
  UseFormBehaviors_Options,
} from 'hooks/useFormBehaviors/useFormBehaviors.types';
import { API_POST_inviteBySpreadsheet } from 'requests/POST_inviteBySpreadsheet';
import { useAPIActions } from 'store/reducers/api/apiSlice';
import { FC, createContext, useCallback, useContext, useMemo, useState } from 'react';
import {
  AddTeamMemberCSVViewSchema,
  CsvError,
} from 'components/InviteTeamMembersByCSVModal/views/ImportViaSpreadsheet/TeamMemberCSVView.constants';
import {
  inviteTeaMemberCSVModalRoutingService,
  useInviteTeamMembersByCSVModalUIContext,
} from 'components/InviteTeamMembersByCSVModal/InviteTeamMembersByCSVModal.ui.context';
import i18next from 'i18next';
import FileSaver from 'file-saver';
import { createBookJaneRequestHeaders } from 'utils/createBookJaneRequest';

type TeamMemberCSVViewFormContextType = UseFormBehaviors<AddTeamMemberCSVViewSchema> & {
  onDownload: () => {};
};

const VALIDATION_CSV = (file: File | undefined) => {
  if (!file) {
    return i18next.t('team_member:FILE_IS_REQUIRED');
  }

  const allowedFileTypes = ['csv', 'xlsx']; // Add your allowed file types here
  const fileNameParts = file.name.split('.');
  const fileExtension = fileNameParts[fileNameParts.length - 1].toLowerCase();
  if (!allowedFileTypes.includes(fileExtension)) {
    return i18next.t('team_member:FILE_NOT_CORRECT_FORMAT_UPLOAD_XLSX_FILE_AND_TRY_AGAIN');
  }

  return '';
};

const TeamMemberCSVViewInitialState = {
  csv: undefined,
  location_ids: [''],
};

export const TeamMemberCSVViewFormContext = createContext<TeamMemberCSVViewFormContextType>(
  {} as TeamMemberCSVViewFormContextType,
);

export const TeamMemberCSVViewFormProvider: FC = ({ children }) => {
  const { apiRequest } = useAPIActions();
  const { goToViewError } = useInviteTeamMembersByCSVModalUIContext();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const handleSubmit = useCallback(
    async (values) => {
      setIsSubmitting(true);
      const formData = new FormData();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { csv } = values as any;
      formData.append('csv_file', csv);
      let collectedErrors: CsvError[] = [];
      apiRequest(API_POST_inviteBySpreadsheet({ formData }), {
        onSuccess: () => {
          setIsSubmitting(false);
          inviteTeaMemberCSVModalRoutingService.goToView('SuccessFul');
        },
        onError: (error) => {
          setIsSubmitting(false);
          const errors = error?.data;
          if (errors) {
            if (Array.isArray(errors)) {
              errors.forEach((errorObj) => {
                collectedErrors.push(errorObj);
              });
              // Assume standard rails validation error
            } else {
              if (errors?.error_messages?.['users_communities.base']) {
                collectedErrors.push({
                  column: '-',
                  row: '-',
                  error: errors?.error_messages?.['users_communities.base']?.[0],
                });
              }
            }
          }
          goToViewError({ csvErrors: collectedErrors });
        },
      });
    },

    [apiRequest, goToViewError],
  );

  const addCSVRuleFormBehaviorsOptions: UseFormBehaviors_Options<AddTeamMemberCSVViewSchema> = {
    initialState: { ...TeamMemberCSVViewInitialState },
    isDirtyCheckEnabled: true,
    type: 'CREATE',
    onSubmit: handleSubmit,
    validations: {
      csv: VALIDATION_CSV,
    },
  };

  const addEditRankingRuleForm = useFormBehaviors<AddTeamMemberCSVViewSchema>(
    addCSVRuleFormBehaviorsOptions,
  );
  const onDownload = useCallback(async () => {
    await fetch(DOWNLOAD_INVITE_SPREADSHEET(), {
      method: 'GET',
      headers: createBookJaneRequestHeaders({}),
    }).then(async (response) => {
      if (response.status > 200) {
        throw response.json();
      } else {
        const CSV = await response.blob();
        FileSaver.saveAs(CSV, 'TeamMemberInviteSpreadsheetTemplate.xlsx');
      }
    });
  }, []);

  const value = useMemo(
    () => ({
      ...addEditRankingRuleForm,
      isSubmitting,
      onChange: (e: UseFormBehaviorsEventType | UseFormBehaviorsEventType[]) => {
        if (Array.isArray(e)) {
          // If e is an array of events, iterate through them
          e.forEach(({ target: { value = '', name = 'csv', ...rest } }) => {
            addEditRankingRuleForm.onChange({ target: { value, name, ...rest } });
          });
        } else {
          const {
            target: { value = '', name = 'csv', ...rest },
          } = e;
          addEditRankingRuleForm.onChange({
            target: { name, value, ...rest },
          });
        }
      },
      onDownload,
    }),
    [addEditRankingRuleForm, isSubmitting, onDownload],
  );
  return (
    <TeamMemberCSVViewFormContext.Provider value={value}>
      {children}
    </TeamMemberCSVViewFormContext.Provider>
  );
};

export const useTeamMemberCSVViewFormContext = (): TeamMemberCSVViewFormContextType =>
  useContext(TeamMemberCSVViewFormContext);
